import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import './ProfilePage.css';
import ResultModal from './ResultModal';

interface TestSession {
  sessionId: number;
  userId: number;
  testId: number;
  status: string;
  startTime: string;
  endTime: string | null;
  currentQuestionId: number | null;
  timeLimit: number | null;
  forMyself: boolean;
  timed: boolean;
  createdAt: string;
  updatedAt: string;
  profileId: number | null;
}

interface Keyword {
  keywordId: number;
  description: {
    tr: {
      description: string;
      name: string;
    };
    en: {
      description: string;
      name: string;
    };
  };
}

interface ProfileDescription {
  tr: {
    longDescription: string;
    shortDescription: string;
    name: string;
  };
  en: {
    longDescription: string;
    shortDescription: string;
    name: string;
  };
}

type Language = 'tr' | 'en';

interface TestResult {
  profile: {
    description: ProfileDescription;
    testId: number;
    profileId: number;
    keywords: Keyword[];
  };
  extraQuestion: any | null;
  askOnlySerial: any | null;
}

const ProfilePage: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const { t } = useTranslation();
  const { language } = useLanguage();
  const currentLang = language.toLowerCase() as Language;
  const [showPassword, setShowPassword] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [userData, setUserData] = useState({
    username: currentUser?.username || '',
    email: currentUser?.email || '',
  });
  const [testSessions, setTestSessions] = useState<TestSession[]>([]);
  const [selectedSession, setSelectedSession] = useState<TestSession | null>(null);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);

  useEffect(() => {
    if (currentUser) {
      fetchTestSessions();
    }
  }, [currentUser]);

  const fetchTestSessions = async () => {
    try {
      setLoading(true);
      console.log('Fetching test sessions...');
      
      // Get user data from localStorage
      const userDataStr = localStorage.getItem('whomii_user');
      if (!userDataStr) {
        throw new Error('User not authenticated');
      }
      
      const userData = JSON.parse(userDataStr);
      const basicAuth = `Basic ${btoa(`${userData.email}:${userData.password}`)}`;
      
      const response = await fetch('https://apiwhomii-z2uc5.ondigitalocean.app/api/v1/test-sessions', {
        headers: {
          'Authorization': basicAuth,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Received test sessions:', data);
      setTestSessions(data);
    } catch (error) {
      console.error('Error fetching test sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTestResult = async (sessionId: number) => {
    try {
      setLoading(true);
      
      // Get user data from localStorage
      const userDataStr = localStorage.getItem('whomii_user');
      if (!userDataStr) {
        throw new Error('User not authenticated');
      }
      
      const userData = JSON.parse(userDataStr);
      const basicAuth = `Basic ${btoa(`${userData.email}:${userData.password}`)}`;
      
      const response = await fetch(`https://apiwhomii-z2uc5.ondigitalocean.app/api/v1/test-sessions/${sessionId}/result`, {
        headers: {
          'Authorization': basicAuth,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setTestResult(data);
    } catch (error) {
      console.error('Error fetching test result:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSessionClick = (session: TestSession) => {
    if (session.status === 'COMPLETED') {
      setSelectedSession(session);
      fetchTestResult(session.sessionId);
      setShowResultModal(true);
    }
  };

  const closeResultModal = () => {
    setShowResultModal(false);
  };

  if (!currentUser) {
    return (
      <div className="profile-container">
        <div className="profile-card">
          <h2>{t('notLoggedIn')}</h2>
          <p>{t('pleaseLogInProfile')}</p>
        </div>
      </div>
    );
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleEditMode = () => {
    setEditMode(!editMode);
    // Reset form data when canceling edit
    if (editMode) {
      setUserData({
        username: currentUser.username,
        email: currentUser.email,
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUserData({
      ...userData,
      [name]: value,
    });
  };

  const handleLogout = async () => {
    try {
      // Call the logout function from AuthContext
      await logout();
      
      // Clear all local storage except theme and language preferences
      const theme = localStorage.getItem('theme');
      const language = localStorage.getItem('language');
      localStorage.clear();
      if (theme) localStorage.setItem('theme', theme);
      if (language) localStorage.setItem('language', language);
      
      // Force a full page reload to clear all state
      window.location.reload();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <div className="profile-container">
      <div className="profile-header">
        <h1>{t('myProfile')}</h1>
        <p>{t('manageAccountInfo')}</p>
      </div>

      <div className="profile-card">
        <div className="profile-avatar">
          <div className="avatar-circle">
            {currentUser.username.charAt(0).toUpperCase()}
          </div>
          <h2>{currentUser.username}</h2>
        </div>

        <div className="profile-info">
          <div className="info-group">
            <label>{t('username')}</label>
            {editMode ? (
              <input
                type="text"
                name="username"
                value={userData.username}
                onChange={handleInputChange}
              />
            ) : (
              <p>{currentUser.username}</p>
            )}
          </div>

          <div className="info-group">
            <label>{t('email')}</label>
            {editMode ? (
              <input
                type="email"
                name="email"
                value={userData.email}
                onChange={handleInputChange}
              />
            ) : (
              <p>{currentUser.email}</p>
            )}
          </div>

          <div className="info-group">
            <label>{t('password')}</label>
            <div className="password-field">
              {showPassword ? (
                <p>{currentUser.password}</p>
              ) : (
                <p>{'•'.repeat(10)}</p>
              )}
              <button 
                className="visibility-toggle"
                onClick={togglePasswordVisibility}
                aria-label={showPassword ? String(t('hidePassword')) : String(t('showPassword'))}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9.9 4.24C10.5883 4.0789 11.2931 3.99836 12 4C19 4 23 12 23 12C22.393 13.1356 21.6691 14.2047 20.84 15.19M14.12 14.12C13.8454 14.4147 13.5141 14.6512 13.1462 14.8151C12.7782 14.9791 12.3809 15.0673 11.9781 15.0744C11.5753 15.0815 11.1752 15.0074 10.8016 14.8565C10.4281 14.7056 10.0887 14.4811 9.80385 14.1962C9.51897 13.9113 9.29439 13.572 9.14351 13.1984C8.99262 12.8249 8.91853 12.4247 8.92563 12.0219C8.93274 11.6191 9.02091 11.2219 9.18488 10.8539C9.34884 10.4859 9.58525 10.1547 9.88 9.88M1 1L23 23M17.94 17.94C16.2306 19.243 14.1491 19.9649 12 20C5 20 1 12 1 12C2.24389 9.68192 3.96914 7.65663 6.06 6.06L17.94 17.94Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="profile-actions">
          <button className="action-button edit-button" onClick={toggleEditMode}>
            {editMode ? t('cancel') : t('editProfile')}
          </button>
          {editMode && (
            <button className="action-button save-button">
              {t('saveChanges')}
            </button>
          )}
          <button className="action-button logout-button" onClick={handleLogout}>
            {t('logout')}
          </button>
        </div>
      </div>
      
      <div className="security-card">
        <h3>{t('accountSecurity')}</h3>
        <div className="security-info">
          <div className="security-item">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 16V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 8H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <div>
              <h4>{t('autoGeneratedAccount')}</h4>
              <p>{t('temporaryAccount')}</p>
            </div>
          </div>
          <div className="security-item">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 16V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 8H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <div>
              <h4>{t('dataStorage')}</h4>
              <p>{t('localStorageInfo')}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="test-history-card">
        <h3>{t('testHistory')}</h3>
        {loading ? (
          <div className="loading-spinner">{t('loading')}</div>
        ) : testSessions.length === 0 ? (
          <div className="no-sessions">
            <p>{t('testHistoryEmpty')}</p>
          </div>
        ) : (
          <div className="test-sessions">
            {testSessions.map((session) => (
              <div
                key={session.sessionId}
                className={`test-session ${selectedSession?.sessionId === session.sessionId ? 'selected' : ''}`}
                onClick={() => handleSessionClick(session)}
              >
                <div className="session-info">
                  <h4>{t('test')} #{session.testId}</h4>
                  <p>{t('status')}: {session.status}</p>
                  <p>{t('started')}: {new Date(session.startTime).toLocaleDateString()}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showResultModal && testResult && selectedSession?.status === 'COMPLETED' && (
        <ResultModal
          isOpen={showResultModal}
          onClose={closeResultModal}
          title={t('testResults')}
        >
          <div className="result-content">
            <h4 className="result-profile-name">{testResult.profile.description[currentLang].name}</h4>
            <p className="result-short-desc">{testResult.profile.description[currentLang].shortDescription}</p>
            
            <div className="result-long-desc">
              <h5>{t('detailedDescription')}</h5>
              <p>{testResult.profile.description[currentLang].longDescription}</p>
            </div>
            
            <div className="result-keywords">
              <h5>{t('keywords')}</h5>
              <div className="keywords-flex">
                {testResult.profile.keywords.map((keyword) => (
                  <div key={keyword.keywordId} className="keyword-chip">
                    <span className="keyword-name">{keyword.description[currentLang].name}</span>
                    <div className="keyword-tooltip">
                      <p>{keyword.description[currentLang].description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </ResultModal>
      )}
    </div>
  );
};

export default ProfilePage; 